import { Component } from '@theme/component';

/**
 * A custom element that renders a video background.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  #controller = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    // disconnectedCallback() aborts #controller; create a fresh one each connect so the
    // loadeddata listener still registers (and the video still autoplays) after a reconnect.
    this.#controller = new AbortController();

    const { videoSources, videoElement } = this.refs;

    const startPlayback = () => {
      const playPromise = videoElement.play();

      if (playPromise instanceof Promise) {
        playPromise.catch(() => {});
      }
    };

    for (const source of videoSources) {
      const { videoSource } = source.dataset;

      if (videoSource) source.setAttribute('src', videoSource);
    }

    videoElement.defaultMuted = true;
    videoElement.muted = true;
    videoElement.autoplay = true;
    videoElement.playsInline = true;
    videoElement.addEventListener('loadeddata', startPlayback, { once: true, signal: this.#controller.signal });
    videoElement.load();
    startPlayback();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    // Drop the pending loadeddata listener and stop playback if we're removed before/after load.
    this.#controller.abort();
    this.refs.videoElement?.pause();
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
